import { computed, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../../auth/services/auth.service';
import { SchoolService } from '../../../schools/services/school-service/school-service';
import { SvpDetailService } from '../../../svp-detail/services/svp-detail-service/svp-detail.service';
import { ActiveLicense } from '../../../school-license/models/school-license.model';
import { LicenseFeature, tierAllows } from '../../../school-license/models/license-feature.model';
import { NotificationService } from '../notification-service/notification-service';
import { problemCode, ProblemDetail } from '../../models/problem-detail.model';
import { ApiErrorCode } from '../../errors/api-error-code';

/**
 * How long a refresh of the school list counts as recent enough. One user action can fire several
 * guarded requests at once (the ŠVP detail shell does), and each expiry refusal would otherwise
 * trigger its own `GET /schools`. Long enough to cover such a burst, short enough that a licence
 * granted in the meantime is picked up on the next attempt.
 */
const SCHOOL_RELOAD_WINDOW_MS = 1000;

/**
 * How long a charged AI call waits before the school list is refetched. A single action can charge
 * several calls in a row, and the balance only has to be right once they are all through — the
 * delay coalesces such a burst into one `GET /schools`.
 */
const AI_CHARGE_RELOAD_DELAY_MS = 500;

/** The explanation shown for each refusal the server can answer a guarded request with. */
const REFUSAL_MESSAGES = new Map<string, string>([
  [ApiErrorCode.SCHOOL_LICENSE_INACTIVE, 'Platnost licence školy skončila. Změny se neuložily.'],
  [
    ApiErrorCode.LICENSE_TIER_INSUFFICIENT,
    'Tato funkce není součástí licence školy. Pro rozšíření licence nás kontaktujte.',
  ],
  [
    ApiErrorCode.AI_QUOTA_EXCEEDED,
    'Škole došel kredit pro generování pomocí AI. Ostatní úpravy fungují dál.',
  ],
]);

export const READ_ONLY_TOOLTIP = 'Škola nemá platnou licenci';

/**
 * The license of the school the user is currently working in as a cross-cutting application
 * state: whether they may change anything at all ({@link readOnly}) and which of the tiered
 * features their license reaches ({@link featureBlocked}). Both are read from the `activeLicense`
 * that `GET /schools` already carries, so no extra request is needed to render a form in the
 * right mode.
 */
@Injectable({
  providedIn: 'root',
})
export class LicenseStateService {
  private readonly schoolService = inject(SchoolService);
  private readonly svpDetailService = inject(SvpDetailService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private lastSchoolReloadAt = 0;
  private pendingChargeReload: ReturnType<typeof setTimeout> | null = null;

  /**
   * The school the user is working in — the one selected in the header. An open ŠVP does not
   * override it: `SvpDetailService` moves the selection to the ŠVP's school as it loads, so the
   * header, this service and every page agree on one school instead of each reading its own.
   *
   * `null` until the school list has arrived — and while an open ŠVP is still loading: until it
   * has, the selection is whatever the last visit left behind, and a deep link into another
   * school's ŠVP would be judged by that school for the load window, locking a licensed school
   * out of its own data or opening an unlicensed one's for a moment.
   */
  private readonly currentSchool = computed(() => {
    if (this.svpDetailService.activeSvpId() !== null && this.svpDetailService.svp() === undefined) {
      return null;
    }
    return this.schoolService.selectedSchool();
  });

  readonly currentSchoolId = computed(() => this.currentSchool()?.id ?? null);

  readonly activeLicense = computed(() => this.currentSchool()?.activeLicense ?? null);

  /**
   * The school may read everything but change nothing. Stays `false` while the school is not
   * known yet — there is nothing to be read-only about.
   */
  readonly readOnly = computed(
    () => this.currentSchool() !== null && this.activeLicense() === null,
  );

  /**
   * Whether the current school's tier is one this feature is sold with. Only ever true for a
   * school known to hold a license the feature is missing from: a missing license is not a tier
   * problem ({@link readOnly} covers it) and a school list still loading is not one either — a
   * control that locks itself in either case advertises an upgrade that is not the obstacle.
   */
  featureBlocked(feature: LicenseFeature): boolean {
    const license = this.activeLicense();
    return license !== null && !tierAllows(feature, license.type);
  }

  /**
   * Whether a feature stays out of reach, a school without any license included. Unlike
   * {@link featureBlocked} — written for a control that a missing license already disables through
   * {@link readOnly} — this is the question content asks: a course sold with PREMIUM is no more
   * readable without a license than with the wrong one, so both answer the same.
   *
   * A school still loading counts as blocked, so the lock never opens for a moment on a card the
   * user could click through in the meantime.
   */
  featureUnavailable(feature: LicenseFeature): boolean {
    const license = this.activeLicense();
    return license === null || !tierAllows(feature, license.type);
  }

  /**
   * The same two states for a school addressed explicitly, e.g. by a route parameter or by the row
   * of a list that spans schools — neither of which follows {@link currentSchool}. Reads a signal,
   * so calling it from a `computed` keeps the result reactive.
   */
  licenseOf(schoolId: string | null): ActiveLicense | null {
    if (schoolId === null) return null;
    return this.schoolService.schools().find((s) => s.id === schoolId)?.activeLicense ?? null;
  }

  readOnlyFor(schoolId: string | null): boolean {
    const school = this.schoolService.schools().find((s) => s.id === schoolId);
    return school !== undefined && school.activeLicense === null;
  }

  featureBlockedFor(feature: LicenseFeature, schoolId: string | null): boolean {
    const license = this.licenseOf(schoolId);
    return license !== null && !tierAllows(feature, license.type);
  }

  /** {@link featureUnavailable} for a school addressed explicitly; an unknown school is blocked. */
  featureUnavailableFor(feature: LicenseFeature, schoolId: string | null): boolean {
    const license = this.licenseOf(schoolId);
    return license === null || !tierAllows(feature, license.type);
  }

  /**
   * Reacts to the three license refusals the server can answer with. Returns whether the error was
   * one of them, so that a call site with its own recovery can skip its generic handling.
   */
  handleLicenseError(error: HttpErrorResponse): boolean {
    const problem = error.error as ProblemDetail | null;
    const code = problemCode(problem) ?? '';
    const fallback = REFUSAL_MESSAGES.get(code);
    if (fallback === undefined) return false;

    // A tier refusal comes with a Czech `detail` naming the tiers that would work — more use than
    // anything phrased here, and it stays right when the tiers change. An empty one is no message
    // at all, so it falls back rather than opening a blank snack bar.
    const detail = problem?.detail?.trim();
    const message = code === ApiErrorCode.LICENSE_TIER_INSUFFICIENT && detail ? detail : fallback;

    if (code === ApiErrorCode.SCHOOL_LICENSE_INACTIVE) {
      // The license may have expired mid-session; refresh so the read-only banner appears.
      this.reloadSchoolsOnce();
    }
    // Shown for every request of a burst rather than only the first: the snack bar holds one
    // message at a time, and each repetition renews the window that keeps the generic "Chyba
    // při…" of that request from replacing this explanation.
    this.notificationService.showBlockingError(message);
    return true;
  }

  /**
   * An AI call has just been charged to the pool of the current school, which makes the
   * `remainingCredits` of the loaded school list stale — nothing else tells the application that
   * the balance moved. Called by `aiChargeInterceptor` for every response that reports a charge.
   */
  noteAiCharge(): void {
    if (this.pendingChargeReload !== null) return;
    this.pendingChargeReload = setTimeout(() => {
      this.pendingChargeReload = null;
      // The session can end inside the delay — by the user logging out or by `authInterceptor`
      // doing it on an expired token. Refetching then would send `GET /schools` without a token
      // and answer the login screen with a 401.
      if (!this.authService.isAuthenticated()) return;
      this.schoolService.reloadSchools();
    }, AI_CHARGE_RELOAD_DELAY_MS);
  }

  private reloadSchoolsOnce(): void {
    const now = Date.now();
    if (now - this.lastSchoolReloadAt < SCHOOL_RELOAD_WINDOW_MS) return;
    this.lastSchoolReloadAt = now;
    this.schoolService.reloadSchools();
  }
}
